import { Close, Edit } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import PermissionTooltip from 'components/Permission/PermissionTooltip';
import TargetHandler from 'handlers/targets.handler';
import { useRoleBasedAccess } from 'hooks/useRoleBasedAccess';
import { ThemePalette } from 'mui.theme';
import { useEffect, useState } from 'react';
import {
  CLIENT_THEME,
  clientThemePalette,
  isYokohamaTheme,
} from 'utils/clientTheme';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';

interface Props {
  enableDarkTheme: boolean;
  assetType: 'all' | 'loader' | 'hauler';
  metricName?: 'idling' | 'utilization' | 'production';
  changeTarget?: any;
  loading?: boolean;
}

const TargetSetter = ({
  enableDarkTheme,
  assetType,
  metricName = 'utilization',
  changeTarget,
  loading = false,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const targetButtonTextColor =
    isYokohamaTheme() && enableDarkTheme ? palette.white : palette.textPrimary;
  // role based access for edit
  const isUserAllowed = useRoleBasedAccess();

  const initialTargetValue = useAppSelector(
    (state) => state?.targetReducer?.data[metricName]
  )?.find((item) => item.assetType === assetType)?.targetValue as number;
  const isLoadingTarget = useAppSelector(
    (state) => state?.targetReducer.loadingStates?.isLoadingTarget
  );
  const isLoading =
    metricName === 'idling'
      ? useAppSelector((state) => state?.driverBehaviorReducer.isLoading)
      : isLoadingTarget;
  const customerId = useAppSelector((state) => state.persistedReducer).customer
    ?.id;
  const targetHandler = new TargetHandler();

  const [open, setOpen] = useState(false);

  const toggleOpen = () => {
    setOpen(!open);
  };

  const [targetValue, setTargetValue] = useState<number | string>(
    initialTargetValue
  );

  const handleTargetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '' || e?.target?.value === 'e') {
      setTargetValue('');
      return;
    } else if (isNaN(Number(e.target.value))) {
      return;
    }
    setTargetValue(Number(e.target.value));
  };

  const handleTargetSubmit = async () => {
    try {
      if (!targetValue) {
        return;
      }
      await targetHandler.postTarget(
        customerId,
        metricName,
        +targetValue,
        new Date().getFullYear(),
        assetType
      );
      toggleOpen();
      if (changeTarget) {
        changeTarget(targetValue);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleKeyDownForEKey = (e: React.KeyboardEvent) => {
    if (e.key === 'e') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    setTargetValue(initialTargetValue);
  }, [initialTargetValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {/* <Typography
        sx={{
          fontSize: '12px',
        }}
      >
        Mine Production Plan:
      </Typography> */}
      <PermissionTooltip show={!isUserAllowed}>
        <Button
          size="small"
          sx={{
            border:
              isLoadingTarget || isLoading
                ? '1px solid #606161'
                : `1px solid ${palette.primary}`,
            color: targetButtonTextColor,
            textTransform: 'none',
            fontSize: '11px',
            width: '264px',
            height: '32px',
            '& .MuiButton-startIcon': {
              color: targetButtonTextColor,
            },
            '&.Mui-disabled': {
              color: targetButtonTextColor,
              opacity: 0.6,
            },
            '&.Mui-disabled .MuiButton-startIcon': {
              color: targetButtonTextColor,
            },
          }}
          startIcon={<Edit />}
          onClick={toggleOpen}
          disabled={!isUserAllowed || isLoadingTarget || isLoading || loading}
        >
          Set{' '}
          {metricName === 'idling'
            ? Number.isFinite(initialTargetValue)
              ? ` Short Idling Threshold : ${initialTargetValue} secs`
              : ' Short Idling Threshold'
            : ' annual target'}
        </Button>
      </PermissionTooltip>
      <Dialog
        open={open}
        onClose={toggleOpen}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: palette.boxBackground,
          },
          p: 4,
        }}
      >
        <DialogTitle
          sx={{
            color: ThemePalette.typography.black,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">
              Set {metricName === 'idling' ? 'Time(in secs)' : 'Target'}
            </Typography>
            <IconButton
              onClick={toggleOpen}
              sx={{
                color: ThemePalette.typography.black,
                borderRadius: '8px',
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            p: 2,
            minWidth: '250px',
          }}
        >
          <TextField
            size="small"
            sx={{}}
            label={t('target')}
            variant="outlined"
            type="number"
            value={targetValue}
            onChange={handleTargetChange}
            disabled={isLoadingTarget || isLoading || loading}
            onKeyDown={handleKeyDownForEKey}
            inputProps={{
              min: 0,
              onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
                const maxValue =
                  metricName === 'idling'
                    ? 86400
                    : metricName === 'utilization'
                    ? 8760
                    : undefined;
                if (maxValue !== undefined) {
                  e.target.value = Math.min(
                    maxValue,
                    Math.max(0, parseFloat(e.target.value) || 0)
                  ).toString();
                }
              },
              max:
                metricName === 'idling'
                  ? 86400
                  : metricName === 'utilization'
                  ? 8760
                  : undefined,
            }}
          />
          <Button
            variant="contained"
            onClick={handleTargetSubmit}
            disabled={isLoadingTarget || isLoading || loading}
          >
            {t('save')}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default TargetSetter;
